// Fit intelligence — real-world measurement from face landmarks.
//
// Calibration anchor: the human iris is ~11.7 mm in diameter and varies little
// between adults, so it's a more reliable on-face ruler than guessing from the
// canonical model. Gives ±~1-1.5 mm PD on a flat webcam — good enough to pick a
// frame, NOT a substitute for an optometrist (the UI says so).

import type { FrameAsset, Measurement } from './types';
import { IDX, type LM } from './landmarks';

const IRIS_DIAMETER_MM = 11.7;

function px(a: LM, b: LM, w: number, h: number): number {
  return Math.hypot((a.x - b.x) * w, (a.y - b.y) * h);
}

export interface FrameMeasurement {
  pdMm: number;
  faceWidthMm: number;
}

/** Single-frame estimate. Returns null if iris landmarks look degenerate. */
export function measureFrame(
  lm: LM[],
  videoW: number,
  videoH: number,
): FrameMeasurement | null {
  const leftIrisPx = px(lm[IDX.leftIrisRight], lm[IDX.leftIrisLeft], videoW, videoH);
  const rightIrisPx = px(lm[IDX.rightIrisRight], lm[IDX.rightIrisLeft], videoW, videoH);
  const irisPx = (leftIrisPx + rightIrisPx) / 2;
  if (irisPx < 2) return null; // face too far / bad detection

  const mmPerPx = IRIS_DIAMETER_MM / irisPx;
  const pdMm = px(lm[IDX.leftIrisCenter], lm[IDX.rightIrisCenter], videoW, videoH) * mmPerPx;
  const faceWidthMm = px(lm[IDX.leftCheek], lm[IDX.rightCheek], videoW, videoH) * mmPerPx;

  // Sanity gate — reject physically implausible reads (extreme angle / glitch).
  if (pdMm < 45 || pdMm > 80 || faceWidthMm < 110 || faceWidthMm > 180) return null;
  return { pdMm, faceWidthMm };
}

function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/**
 * Accumulates per-frame estimates and exposes a stable median-over-N reading
 * with a confidence score derived from sample spread (tight cluster → high
 * confidence). The UI waits for confidence before presenting the PD.
 */
export class MeasurementAccumulator {
  private pd: number[] = [];
  private fw: number[] = [];
  constructor(
    private window = 30,
    private minSamples = 12,
  ) {}

  push(m: FrameMeasurement) {
    this.pd.push(m.pdMm);
    this.fw.push(m.faceWidthMm);
    if (this.pd.length > this.window) {
      this.pd.shift();
      this.fw.shift();
    }
  }

  reset() {
    this.pd = [];
    this.fw = [];
  }

  read(): Measurement {
    const samples = this.pd.length;
    if (samples < this.minSamples) {
      return { pdMm: null, faceWidthMm: null, confidence: 0, samples };
    }
    const pdMm = median(this.pd);
    const faceWidthMm = median(this.fw);
    // Confidence from relative spread of PD samples (mean abs deviation / median).
    const mad = this.pd.reduce((acc, v) => acc + Math.abs(v - pdMm), 0) / samples;
    const confidence = Math.max(0, Math.min(1, 1 - (mad / pdMm) * 12));
    return { pdMm, faceWidthMm, confidence, samples };
  }
}

export type FitLabel = 'narrow' | 'fits' | 'wide';

export interface FitResult {
  label: FitLabel;
  ratio: number; // frame front width / measured face width
  frameWidthMm: number;
}

/**
 * Compare the frame's physical front width (2×lens + bridge) against the
 * measured face width. Thresholds chosen so a frame within ~±3% of face width
 * reads as a good fit.
 */
export function computeFit(frame: FrameAsset, faceWidthMm: number | null): FitResult | null {
  if (!faceWidthMm) return null;
  const frameWidthMm = 2 * frame.lensWidthMm + frame.bridgeMm;
  const ratio = frameWidthMm / faceWidthMm;
  const label: FitLabel = ratio < 0.88 ? 'narrow' : ratio > 1.03 ? 'wide' : 'fits';
  return { label, ratio, frameWidthMm };
}
